/**
 * Die fünf Phasen einer Reise — und welcher Reiter zu welcher gehört.
 *
 * ## Warum es diese Datei gibt
 *
 * Bis zum 14.9.2026 standen elf Reiter in einer Leiste über dem Planer, in
 * zwei Gruppen mit den Namen „Planen“ und „Drumherum“. Die Gruppierung war
 * eine Aussage über die **Häufigkeit** („ständig“ gegen „einmal pro Reise“) —
 * und die stimmt nie: Im Entwurf ist „Plan“ der tägliche Reiter, unterwegs
 * „Tagebuch“, danach „Kosten“. Eine Ordnung nach Häufigkeit ist elf Monate im
 * Jahr falsch.
 *
 * Die Phase ist die ehrlichere Ordnung, weil sie sich mit der Reise bewegt:
 * Entwerfen, Rechnen, Vorbereiten, Unterwegs, Erinnern. Jeder Reiter gehört
 * zu genau einer davon, und welche Phase gerade dran ist, folgt aus
 * `reisephase.ts` — also aus Kalender und Abschlussstand, nicht aus einer
 * Vermutung.
 *
 * ## Warum die Zuordnung hier steht und nicht in der Leiste
 *
 * Aus demselben Grund wie bei `befehle.ts` und `werkbank.ts`: Eine
 * Zuordnung, die in einer Svelte-Komponente steht, ist ohne Browser nicht
 * prüfbar. Genau diese Sorte Regel geht beim Nachbessern still kaputt — ein
 * Reiter, der in keiner Phase steht, verschwindet aus der Navigation, ohne
 * dass irgendetwas rot wird.
 *
 * **Reine Funktionen** — kein DOM, kein Netz, keine Uhr. Der heutige Tag und
 * die Phase kommen von außen herein.
 */
import type { ZeichenName } from '../../gestaltung/Zeichen.svelte';
import type { Reisephase } from '../reisephase';
import { reiterSichtbar as reiterSichtbarNachAntrieb, type Antrieb } from '../reiseart';

/**
 * Ab welcher Fensterbreite die Seitenleiste als Spalte steht.
 *
 * Darunter wird sie zur Schublade hinter dem Menüknopf, und der Planer stellt
 * darunter Karte und Fläche übereinander. Die Zahl steht hier **und** als
 * Medienabfrage in drei Dateien (`Seitenleiste.svelte`, `App.svelte`,
 * `Planner.svelte`); CSS kann keine TypeScript-Konstante lesen, also
 * vergleicht der Test beide Seiten. Ohne das wäre eine der drei eines Tages
 * 860 — und die Navigation spränge in einem Fenster um, in dem der Planer
 * noch das andere Raster zeigt.
 */
export const LEISTE_AB = 820;

/** Die fünf Phasen in ihrer natürlichen Reihenfolge. */
export type Phase = 'entwerfen' | 'rechnen' | 'vorbereiten' | 'unterwegs' | 'erinnern';

/**
 * Die zwölf Reiter des Planers.
 *
 * Der Typ stand bis zum 14.9.2026 in `Planner.svelte`. Er ist hierher
 * gewandert, weil die Navigation ihn braucht und der Planer ihn von hier
 * bezieht — zwei Aufzählungen desselben wären zwei, die auseinanderlaufen.
 *
 * Der zwölfte, `reisebuch`, kam am 7.10.2026 dazu: Das Buch stand bis dahin
 * an der Reisezeile in *Meine Reisen* und gehört in die Reise selbst.
 */
export type Reiterschluessel =
  | 'stationen'
  | 'entdecken'
  | 'kajak'
  | 'plan'
  | 'laden'
  | 'schlafen'
  | 'kosten'
  | 'vorbereitung'
  | 'tagesplaene'
  | 'tagebuch'
  | 'teilen'
  | 'reisebuch';

/** Ein Reiter, wie Leiste und Befehlssuche ihn brauchen. */
export interface Reiterblatt {
  key: Reiterschluessel;
  label: string;
  zeichen: ZeichenName;
}

/** Eine Phase mit ihren Reitern. */
export interface Phasenblatt {
  id: Phase;
  titel: string;
  reiter: readonly Reiterblatt[];
}

/**
 * Die fünf Phasen mit ihren Reitern (Vertrag Navigation A, § 1.2).
 *
 * Die Reihenfolge innerhalb einer Phase ist die Reihenfolge der Arbeit:
 * erst die Orte, dann die Rechnung darauf. Die Zeichen sind dieselben wie in
 * der alten Reiterleiste und in der Befehlssuche — dasselbe Bild für dieselbe
 * Sache, an jeder Stelle.
 *
 * „Vor der Abfahrt“ heißt hier, was im Code `vorbereitung` heißt: Der
 * Schlüssel ist gewachsen, die Beschriftung sagt, wann man die Fläche
 * braucht. Der Schlüssel bleibt unverändert, weil an ihm die Befehlssuche,
 * die Merkung und der Layout-Eintrag des Planers hängen.
 */
export const PHASEN: readonly Phasenblatt[] = [
  {
    id: 'entwerfen',
    titel: 'Entwerfen',
    reiter: [
      { key: 'stationen', label: 'Stationen', zeichen: 'stationen' },
      { key: 'entdecken', label: 'Entdecken', zeichen: 'entdecken' },
      { key: 'kajak', label: 'Kajak', zeichen: 'boot' }
    ]
  },
  {
    id: 'rechnen',
    titel: 'Rechnen',
    reiter: [
      { key: 'plan', label: 'Plan', zeichen: 'karte' },
      { key: 'laden', label: 'Laden', zeichen: 'laden' },
      { key: 'schlafen', label: 'Schlafen', zeichen: 'mond' },
      { key: 'kosten', label: 'Kosten', zeichen: 'euro' }
    ]
  },
  {
    id: 'vorbereiten',
    titel: 'Vorbereiten',
    reiter: [
      { key: 'vorbereitung', label: 'Vor der Abfahrt', zeichen: 'koffer' },
      { key: 'tagesplaene', label: 'Tagespläne', zeichen: 'tagesplan' }
    ]
  },
  {
    id: 'unterwegs',
    titel: 'Unterwegs',
    reiter: [{ key: 'tagebuch', label: 'Tagebuch', zeichen: 'buch' }]
  },
  {
    id: 'erinnern',
    titel: 'Erinnern',
    // Das Reisebuch **hinter** Teilen: Teilen ist der erste Reiter der Phase
    // und damit der, der beim Öffnen einer gefahrenen Reise oben liegt
    // (`vorgabeReiter`). Das Buch ist privat und geht an niemanden; es steht
    // trotzdem hier, weil es dieselbe Frage beantwortet — was bleibt von der
    // Reise? Sichtbar ist es wie Teilen an jeder Reise; ob gesetzt werden
    // kann, sagt das Panel selbst.
    reiter: [
      { key: 'teilen', label: 'Teilen', zeichen: 'teilen' },
      { key: 'reisebuch', label: 'Reisebuch', zeichen: 'buch' }
    ]
  }
];

/** Alle Reiter in Phasenreihenfolge — für Tests und Schleifen. */
export const ALLE_REITER: readonly Reiterblatt[] = PHASEN.flatMap((p) => p.reiter);

/**
 * Das Merkmal, an dem der Kajak-Reiter hängt.
 *
 * Es steht in `settings.merkmale` und schaltet dort schon Packposten
 * (`reiseprofile.merkmaleAus`). Dass es zusätzlich einen Reiter schaltet, ist
 * keine zweite Bedeutung, sondern dieselbe: Diese Reise hat ein Boot dabei.
 */
export const KAJAK_MERKMAL = 'kajak';

/** Was eine Reise über ihre Reiter entscheidet. Absichtlich nicht `Trip`. */
export interface Reiterlage {
  antrieb: Antrieb;
  /** `settings.merkmale`, normiert — siehe `reiseprofile.merkmaleAus`. */
  merkmale: readonly string[];
}

/**
 * Gehört dieser Reiter zu dieser Reise?
 *
 * Zwei Weichen, und sie stehen mit Absicht **hier** und nicht beide in
 * `reiseart.ts`:
 *
 * * `laden` hängt am Antrieb. Diese Regel gehört zur Reiseart, und dort steht
 *   sie auch weiterhin (`reiseart.reiterSichtbar`) — sie entscheidet ja nicht
 *   nur über einen Reiter, sondern beschreibt eine Achse der Reise.
 * * `kajak` hängt am Merkmal. Das ist keine Achse, sondern ein Ausrüstungs-
 *   stück, und es wurde bis zum 14.9.2026 überhaupt nicht ausgewertet: Der
 *   Reiter stand an jeder Reise, auch an denen ohne Boot. Die Regel gehört in
 *   die Navigation, weil sie sonst in `reiseart.ts` als dritte Achse
 *   missverstanden würde (dort steht ausdrücklich, dass es bei **einem**
 *   Reiter bleiben soll).
 *
 * Ohne Merkmalsliste bleibt Kajak weg. Das ist die richtige Richtung: Eine
 * Reise, die nichts über ein Boot sagt, hat keines.
 */
export function reiterGehoert(key: Reiterschluessel, lage: Reiterlage): boolean {
  if (!reiterSichtbarNachAntrieb(key, lage.antrieb)) return false;
  if (key === 'kajak') return lage.merkmale.includes(KAJAK_MERKMAL);
  return true;
}

/**
 * Die Phasen dieser Reise — nur die, die noch Reiter haben.
 *
 * Eine Phase ohne Reiter fällt weg und wird nicht leer gezeigt: Bei einer
 * Verbrenner-Reise ohne Boot hat „Entwerfen“ weiterhin zwei Punkte, aber wenn
 * eine Phase einmal nur aus einem weggefallenen Reiter bestünde, wäre eine
 * leere Überschrift die Ankündigung von nichts.
 */
export function phasenFuer(lage: Reiterlage): Phasenblatt[] {
  return PHASEN.map((p) => ({ ...p, reiter: p.reiter.filter((r) => reiterGehoert(r.key, lage)) })).filter(
    (p) => p.reiter.length > 0
  );
}

/** Zu welcher Phase gehört dieser Reiter? Wirft bei einem unbekannten. */
export function phaseVon(key: Reiterschluessel): Phase {
  const treffer = PHASEN.find((p) => p.reiter.some((r) => r.key === key));
  if (!treffer) throw new Error(`Der Reiter ${key} steht in keiner Phase.`);
  return treffer.id;
}

/** Das Blatt eines Reiters — Beschriftung und Zeichen. Wirft bei einem unbekannten. */
export function reiterblatt(key: Reiterschluessel): Reiterblatt {
  const treffer = ALLE_REITER.find((r) => r.key === key);
  if (!treffer) throw new Error(`Kein Reiter ${key}.`);
  return treffer;
}

/**
 * Welche Phase gerade dran ist (Vertrag § 1.2).
 *
 * Die Reisephase sagt, wo die Reise im Kalender steht; hier wird daraus die
 * Phase der Arbeit:
 *
 * * `nachlese` → **Erinnern**. Die Reise ist gefahren oder abgehakt.
 * * `unterwegs` → **Unterwegs**.
 * * `bald` → **Vorbereiten**. Zwei Wochen vorher wird gebucht und gepackt,
 *   nicht mehr umgeplant (`reisephase.BALD_TAGE`).
 * * `entwurf` → **Entwerfen** ohne Strecke, **Rechnen** mit Strecke.
 *
 * Die letzte Zeile ist die einzige, die nicht am Kalender hängt, und sie ist
 * die wichtigste: Solange keine zwei Stationen stehen, gibt es nichts zu
 * rechnen — ein Plan ohne Strecke ist eine leere Tabelle, und ein Ladeplan
 * ohne Strecke eine Absage. Sobald es eine Strecke gibt, ist das Entwerfen im
 * Kern durch, und die Fragen wechseln von „wohin“ zu „geht das aus“.
 */
export function jetztPhase(phase: Reisephase, hatStrecke: boolean): Phase {
  if (phase === 'nachlese') return 'erinnern';
  if (phase === 'unterwegs') return 'unterwegs';
  if (phase === 'bald') return 'vorbereiten';
  return hatStrecke ? 'rechnen' : 'entwerfen';
}

/** `2026-09-18` → `18.9.` — die kurze Form für die Leiste. */
export function kurztag(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  if (!m) return '';
  return `${Number(m[3])}.${Number(m[2])}.`;
}

/**
 * Das leise Wort rechts an einer Phase (Vertrag § 1.2).
 *
 * Vier Antworten, und jede sagt etwas anderes: `fertig` (liegt hinter der
 * laufenden Phase), `jetzt` (die laufende), `ab 18.9.` (kommt noch und hat
 * einen Termin), `danach` (kommt noch und hat keinen).
 *
 * Den Termin bekommt ausschließlich die Phase **Unterwegs**, und zwar den
 * Reisebeginn: Es ist die einzige der fünf, die wirklich an einem Datum
 * anfängt. „Rechnen ab 18.9.“ wäre eine Frist, die niemand gesetzt hat.
 *
 * `fertig` heißt hier ausdrücklich **nicht** „erledigt“ — es heißt „liegt
 * hinter dir“. Eine Phase weiß nicht, ob ihre Arbeit getan ist; sie weiß nur,
 * wo die Reise steht. Ein Wort, das Vollständigkeit behauptete, wäre an einer
 * halb geplanten Reise eine Lüge.
 */
export function zustandswort(
  phase: Phase,
  jetzt: Phase,
  beginn?: string | null
): string {
  const reihe = PHASEN.map((p) => p.id);
  const hier = reihe.indexOf(phase);
  const dran = reihe.indexOf(jetzt);
  if (hier < dran) return 'fertig';
  if (hier === dran) return 'jetzt';
  const tag = phase === 'unterwegs' ? kurztag(beginn) : '';
  return tag ? `ab ${tag}` : 'danach';
}

/**
 * Der Schlüssel, unter dem der zuletzt offene Reiter einer Reise liegt.
 *
 * Je Reise und nicht global: Wer in Reise 34 im Tagebuch war und Reise 19
 * öffnet, will dort nicht im Tagebuch landen — die beiden stehen in
 * verschiedenen Phasen ihres Lebens.
 */
export function merkungsSchluessel(reiseId: number): string {
  return `reise.reiter.${reiseId}`;
}

/**
 * Welcher Reiter beim Öffnen einer Reise oben liegt (Vertrag § 1.2).
 *
 * Die Reihenfolge der Prüfungen ist die Aussage:
 *
 * 1. **Der gemerkte Reiter gewinnt**, wenn es ihn in dieser Reise gibt. Wer
 *    gestern im Ladeplan aufgehört hat, macht dort weiter — das ist die
 *    stärkste Auskunft darüber, was jemand vorhat, und sie ist von ihm selbst.
 * 2. Sonst der **erste Reiter der laufenden Phase**. Das ist die Antwort auf
 *    die Roadmap-Zeile „Reiter-Vorgabe nach Reisephase“: Am Reisetag steht
 *    nicht mehr die Stationsliste vorn, sondern das Tagebuch.
 * 3. Sonst `stationen` — der Rückfall, der immer da ist.
 *
 * Ein gemerkter Reiter, den es nicht mehr gibt (Kajak ohne Boot, Laden am
 * Verbrenner), fällt durch und wird **nicht** gelöscht: Wer die Reiseart
 * zurückstellt, findet seinen Reiter wieder.
 */
export function vorgabeReiter(
  lage: Reiterlage,
  jetzt: Phase,
  gemerkt?: string | null
): Reiterschluessel {
  const sichtbar = phasenFuer(lage);
  const gefunden = ALLE_REITER.find((r) => r.key === gemerkt);
  if (gefunden && reiterGehoert(gefunden.key, lage)) return gefunden.key;
  const phase = sichtbar.find((p) => p.id === jetzt);
  if (phase) return phase.reiter[0].key;
  return 'stationen';
}
